/**
 * DOCUMENTATION:
 * Component: Html5Dashboard
 * Purpose: Acts as the central hub for the HTML5 curriculum. It features a video tutorial,
 * a self-study timer, a progress tracker, and a searchable list of HTML5 curriculum modules.
 * * Note for Junior Developers:
 * - We are using a styled-components approach via MUI's `styled` utility to keep JSX clean (Clean Code principle).
 * - `useMemo` is used for the `filteredLessons` to prevent unnecessary recalculations on every render unless `searchQuery` changes.
 * - Ensure the custom components (LanguageTabs, LectureTimer, RadioOption) are correctly imported from your project structure.
 */

import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
    Stack,
    styled,
    useTheme,
    useMediaQuery,
    TextField,
    InputAdornment,
    Fab,
    Zoom,
    LinearProgress,
} from "@mui/material";

/* Icons */
import CodeIcon from "@mui/icons-material/Code";
import PersonalVideoIcon from "@mui/icons-material/PersonalVideo";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

/* Custom Components */
// Adjust these relative paths based on your actual project directory structure
import RadioOption from "../../../components/RadioOption";
import Typography from "../../../components/Typography";
import LanguageTabs from "../../../components/LanguageTabs";
import LectureTimer from "../../../components/LectureTimer";

/* Premium Option‑A Background */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* Styled Components - Clean Code Principles applied here */

// Main wrapper creating the dark gradient background
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.90), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(10),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(12),
        paddingBottom: theme.spacing(5),
    },
}));

// Re-using the GlassCard concept for consistent content blocks
const GlassCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    height: "100%",
}));

// Container for individual topic rows
const TopicBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "10px",
    padding: theme.spacing(1.5, 2),
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "rgba(218, 165, 32, 0.1)",
        borderColor: "goldenrod",
    },
}));

// Standardized action button for curriculum items
const ViewButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 700,
    borderRadius: "999px",
    padding: "6px 20px",
    textTransform: "none",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

export default function Html5Dashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* State Management */
    const [searchQuery, setSearchQuery] = useState("");
    const [showScroll, setShowScroll] = useState(false);
    // In a real application, you would fetch the completedCount from your user database
    const [completedCount, setCompletedCount] = useState(0);

    /* ---------------- HTML5 CURRICULUM LESSONS ---------------- */
    const lessons = [
        { title: "Document Structure & Metadata", link: "/html5-structure-metadata" },
        { title: "Semantic HTML (Accessibility & SEO)", link: "/html5-semantic-html" },
        { title: "Forms, Inputs & Validation", link: "/html5-forms-validation" },
        { title: "Multimedia: Audio, Video & Graphics", link: "/html5-multimedia-graphics" },
        { title: "Web Storage API (Local & Session)", link: "/html5-web-storage" },
        { title: "HTML5 Accessibility Best Practices", link: "/html5-accessibility" },
        { title: "HTML5 Practical Project", link: "/html5-practical-project" },
        { title: "HTML5 Certification Quiz", link: "/html5-quiz" },
    ];

    /* Logic for Filtering & Scroll */
    // Only re-filter when searchQuery changes
    const filteredLessons = useMemo(() => {
        return lessons.filter(lesson =>
            lesson.title.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery]);

    // Calculate progress percentage dynamically
    const progressPercentage = (completedCount / lessons.length) * 100;

    // Handle showing/hiding the scroll-to-top button
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <PageWrapper>
            {/* ---------------- GOLDEN BANNER ---------------- */}
            <Box sx={{ textAlign: "center", mb: 4, mt: 16, px: 2 }}>
                <Typography
                    variant={isMobile ? "h6" : "h3"}
                    sx={{
                        fontWeight: 700,
                        color: "goldenrod",
                        px: { xs: 3, md: 6 },
                        py: 1,
                        borderRadius: "999px",
                        display: "inline-block",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    HTML5 FUNDAMENTALS
                </Typography>
            </Box>

            {/* ---------------- LANGUAGE TABS ---------------- */}
            {/* Assuming your LanguageTabs component accepts the active language string */}
            <LanguageTabs active="HTML5" />

            {/* ---------------- MAIN CONTENT ---------------- */}
            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 }, mt: 2 }}>

                {/* LEFT COLUMN: Video & Dashboard */}
                <Grid item xs={12} md={5}>
                    <GlassCard>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, textAlign: "center", mb: 2, color: "goldenrod" }}
                        >
                            SELF STUDY DASHBOARD
                        </Typography>

                        <Card
                            sx={{
                                width: "100%",
                                backgroundColor: "#f5f5f5",
                                textAlign: "center",
                                padding: { xs: "10px", md: "20px" },
                                borderRadius: "12px",
                                mt: 2,
                            }}
                        >
                            {/* Embedded HTML5 Crash Course Video */}
                            <CardMedia
                                component="iframe"
                                height={isMobile ? "200" : "260"}
                                src="https://www.youtube.com/embed/mU6anWqZJcc" // Generic HTML5 Crash Course Placeholder
                                title="Free HTML5 Tutorial"
                                allowFullScreen
                                sx={{ borderRadius: "8px", border: "none", mb: 2 }}
                            />

                            <CardContent sx={{ color: "#100720" }}>
                                <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                                    Learn Essential Skills for Web Development
                                </Typography>

                                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                                    <PersonalVideoIcon fontSize="small" />
                                    <Typography variant="body2" fontWeight={600}>
                                        Mode: Online Self‑paced
                                    </Typography>
                                </Stack>

                                <Link to="/compiler" style={{ textDecoration: "none" }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<CodeIcon />}
                                        sx={{
                                            bgcolor: "#100720",
                                            color: "white",
                                            fontWeight: "bold",
                                            py: 1.5,
                                            borderRadius: "8px",
                                            "&:hover": { bgcolor: "#1a0c36" }
                                        }}
                                    >
                                        Hands‑on Coding
                                    </Button>
                                </Link>
                            </CardContent>
                        </Card>

                        {/* ---------------- TIMER ---------------- */}
                        <Box sx={{ mt: 3 }}>
                            <LectureTimer />
                        </Box>
                    </GlassCard>
                </Grid>

                {/* RIGHT COLUMN — LESSONS WITH PROGRESS & SEARCH */}
                <Grid item xs={12} md={7}>
                    <GlassCard>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, color:"white", textAlign: "center", mb: 3 }}
                        >
                            HTML5 CURRICULUM
                        </Typography>

                        {/* Progress Bar Section */}
                        <Box sx={{ mb: 3, px: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                <Typography variant="caption" sx={{ color: "goldenrod", fontWeight: "bold" }}>
                                    Overall Progress
                                </Typography>
                                <Typography variant="caption" sx={{ color: "goldenrod", fontWeight: "bold" }}>
                                    {Math.round(progressPercentage)}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progressPercentage}
                                sx={{
                                    height: 8,
                                    borderRadius: 5,
                                    bgcolor: "rgba(255,255,255,0.1)",
                                    "& .MuiLinearProgress-bar": { bgcolor: "goldenrod" }
                                }}
                            />
                        </Box>

                        {/* Search Bar */}
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Find a topic..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ mb: 3 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "goldenrod" }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    color: "white",
                                    bgcolor: "rgba(255,255,255,0.05)",
                                    borderRadius: "8px",
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(218, 165, 32, 0.3)" }
                                }
                            }}
                        />

                        {/* Curriculum List */}
                        <Stack spacing={1.5}>
                            {filteredLessons.length > 0 ? (
                                filteredLessons.map((lesson, i) => (
                                    <TopicBox key={i}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexGrow: 1 }}>
                                            <RadioOption />
                                            <Typography variant="body2" sx={{ fontWeight: 500 , color:"white"}}>
                                                {lesson.title}
                                            </Typography>
                                        </Box>
                                        <Link to={lesson.link} style={{ textDecoration: "none" }}>
                                            <ViewButton size="small">View</ViewButton>
                                        </Link>
                                    </TopicBox>
                                ))
                            ) : (
                                <Typography sx={{ textAlign: "center", opacity: 0.5, py: 4 }}>
                                    No topics found matching "{searchQuery}"
                                </Typography>
                            )}
                        </Stack>
                    </GlassCard>
                </Grid>
            </Grid>

            {/* Floating Scroll to Top */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{
                        position: "fixed",
                        bottom: 25,
                        right: 25,
                        bgcolor: "goldenrod",
                        "&:hover": { bgcolor: "#e0b200" }
                    }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
